export class UColor {
  /**
   * Centralized zone colors for professional CAD look
   */
  static readonly ZONES = {
    base: "#E67E22", // Bas: Orange
    top: "#4A90E2", // Haut: Blue
    sides: "#E0E0E0", // Subtle Steel Gray
    all: "#c0c0c0", // Global/All (Neutral Gray)
  };

  /**
   * Generates a distinct color for measurement rules.
   * Filters out Red, Blue, and Gray hues to ensure visibility against the model.
   * Uses "Vivid" saturated hues for maximum contrast.
   */
  static getRuleHex(index: number): string {
    const safeHues = [
      200, // Deep Sky Blue (if needed as fallbacks)
      60, // Pure Yellow
      120, // Pure Green
      180, // Cyan
      300, // Magenta
      330, // Pink
      45, // Orange
      280, // Purple
    ];

    const h = safeHues[index % safeHues.length];
    // 100% Saturation, 50% Lightness for maximum vividness (not washed out)
    return UColor.hslToHex(h, 100, 50);
  }

  /**
   * Converts HSL to Hex
   */
  static hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  /**
   * Returns a color that is not currently used by active zones or default zones.
   */
  static getUnusedColor(usedColors: string[]): string {
    const candidates = [
      "#F1C40F", // Sun Flower (Yellow)
      "#E67E22", // Carrot (Orange)
      "#9B59B6", // Amethyst (Purple)
      "#1ABC9C", // Turquoise
      "#2ECC71", // Emerald
      "#3498DB", // Peter River (Blue)
      "#E74C3C", // Alizarin (Red)
      "#FF9FF3", // Pink
      "#00D2D3", // Jade
    ];
    for (const c of candidates) {
      if (!usedColors.includes(c)) return c;
    }
    // Fallback to a random color if all candidates are used
    return candidates[Math.floor(Math.random() * candidates.length)];
  }
}
